import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { siteInfo, faqData } from '@/mocks/tvShows';

export default function About() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于夫妻影院',
    description: siteInfo.description,
    url: 'https://www.fuqicinema.com/about',
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    url: 'https://www.fuqicinema.com/',
    description: siteInfo.description,
    email: siteInfo.email,
    foundingDate: siteInfo.founded,
    slogan: siteInfo.slogan,
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '夫妻影院首页', item: 'https://www.fuqicinema.com/' },
      { '@type': 'ListItem', position: 2, name: '关于我们' },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  };

  const features = [
    {
      icon: 'ri-movie-line',
      title: '海量资源',
      desc: '涵盖国产剧、韩剧、美剧、日剧、动漫等多品类，每日更新最新剧集，满足各类追剧需求。',
    },
    {
      icon: 'ri-speed-up-line',
      title: '极速播放',
      desc: '多线路智能切换，秒播流畅不卡顿，高清画质带来极致观影体验，告别加载等待。',
    },
    {
      icon: 'ri-smartphone-line',
      title: '多端适配',
      desc: '全面支持手机、平板、电脑，同时支持DLNA和AirPlay投屏，大屏追剧更过瘾。',
    },
    {
      icon: 'ri-shield-check-line',
      title: '完全免费',
      desc: '所有电视剧资源均免费观看，无需注册无需付费，让每一位剧迷都能畅享追剧的快乐。',
    },
  ];

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <Navbar />
      <main className="pt-20 md:pt-24 pb-10">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
            <Link to="/" className="hover:text-accent-500 transition-colors cursor-pointer">首页</Link>
            <span>/</span>
            <span className="text-foreground-300">关于我们</span>
          </nav>

          <section className="relative rounded-2xl overflow-hidden mb-12 md:mb-16">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20cozy%20living%20room%20with%20large%20TV%20screen%20showing%20cinematic%20movie%20scene%2C%20soft%20ambient%20lighting%2C%20comfortable%20sofa%20with%20blankets%2C%20evening%20atmosphere%2C%20cozy%20home%20cinema%20experience%2C%20warm%20golden%20and%20amber%20tones%2C%20intimate%20and%20relaxing%20mood&width=1600&height=500&seq=about-hero-01&orientation=landscape"
              alt="夫妻影院 - 追剧时光"
              className="w-full h-[240px] md:h-[400px] object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background-950/70 via-background-950/40 to-background-950/20 flex items-center">
              <div className="px-6 md:px-12 max-w-xl">
                <h1 className="text-2xl md:text-4xl font-bold text-foreground-50 font-heading mb-3">
                  关于夫妻影院
                </h1>
                <p className="text-sm md:text-base text-foreground-300 leading-relaxed">
                  {siteInfo.slogan}
                </p>
              </div>
            </div>
          </section>

          <section className="mb-12 md:mb-16" aria-label="平台优势">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {features.map((feat, idx) => (
                <div
                  key={idx}
                  className="bg-background-200/50 border border-background-300/40 rounded-xl p-5 md:p-6 hover:border-accent-500/30 transition-all duration-300"
                >
                  <div className="w-10 h-10 rounded-lg bg-accent-500/10 flex items-center justify-center mb-4">
                    <i className={`${feat.icon} text-accent-500 text-xl`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-100 mb-2">
                    {feat.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {feat.desc}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-12 md:mb-16" aria-label="品牌介绍">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-lg md:text-xl font-bold text-foreground-100 font-heading mb-4 text-center">
                品牌介绍
              </h2>
              <div className="bg-background-200/50 border border-background-300/40 rounded-xl p-6 md:p-8">
                <p className="text-sm md:text-base text-foreground-300 leading-relaxed mb-4">
                  夫妻影院成立于{siteInfo.founded}，是国内领先的免费电视剧在线观看平台。自成立以来，
                  我们始终坚持以用户为中心，为每一位剧迷提供海量优质的电视剧资源和极致的观影体验。
                </p>
                <p className="text-sm md:text-base text-foreground-300 leading-relaxed mb-4">
                  平台汇聚了国产剧、韩剧、美剧、日剧、动漫等数十万部高清影视资源，涵盖古装、都市、
                  悬疑、爱情、热血、奇幻等全品类内容。我们拥有一支专业的内容运营团队，每日实时更新
                  最新剧集，确保用户能在第一时间观看到最热门的电视剧。
                </p>
                <p className="text-sm md:text-base text-foreground-300 leading-relaxed">
                  凭借稳定的播放线路和友好的用户界面，夫妻影院已发展成为{siteInfo.views}的头部影视平台。
                  无论是最新上线的热播大剧，还是经典好剧的高分神作，每一位用户都能在夫妻影院找到
                  属于自己的追剧时光。
                </p>
              </div>
            </div>
          </section>

          <section className="mb-12 md:mb-16" aria-label="平台数据">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-lg md:text-xl font-bold text-foreground-100 font-heading mb-6 text-center">
                用户口碑
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { number: '10万+', label: '影视资源' },
                  { number: siteInfo.views, label: '日均播放' },
                  { number: '99.9%', label: '播放成功率' },
                  { number: '4.9', label: '用户评分' },
                ].map((stat, idx) => (
                  <div key={idx} className="text-center bg-background-200/50 border border-background-300/40 rounded-xl p-4 md:p-6">
                    <div className="text-xl md:text-2xl font-bold text-accent-500 font-heading mb-1">
                      {stat.number}
                    </div>
                    <div className="text-xs md:text-sm text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="mb-12 md:mb-16" aria-label="常见问题">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-lg md:text-xl font-bold text-foreground-100 font-heading mb-6 text-center">
                常见问题
              </h2>
              <div className="space-y-3">
                {faqData.map((faq, idx) => (
                  <details
                    key={idx}
                    className="group bg-background-200/50 rounded-lg border border-background-300/40 overflow-hidden cursor-pointer"
                  >
                    <summary className="flex items-center justify-between px-5 py-4 text-sm md:text-base font-medium text-foreground-200 cursor-pointer">
                      {faq.question}
                      <div className="w-5 h-5 flex items-center justify-center ml-3 transition-transform duration-300 group-open:rotate-180">
                        <i className="ri-arrow-down-s-line text-foreground-500"></i>
                      </div>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-500 leading-relaxed">{faq.answer}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          <section aria-label="联系方式">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-lg md:text-xl font-bold text-foreground-100 font-heading mb-6 text-center">
                联系我们
              </h2>
              <div className="bg-background-200/50 border border-background-300/40 rounded-xl p-6 md:p-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-accent-500/10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-accent-500 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-200 mb-1">电子邮箱</p>
                      <p className="text-sm text-foreground-500">{siteInfo.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-accent-500/10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-accent-500 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-200 mb-1">服务时间</p>
                      <p className="text-sm text-foreground-500">7×24小时在线</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-accent-500/10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-refresh-line text-accent-500 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-200 mb-1">内容更新</p>
                      <p className="text-sm text-foreground-500">每日同步更新</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-accent-500/10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-heart-line text-accent-500 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-200 mb-1">使用费用</p>
                      <p className="text-sm text-foreground-500">完全免费</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}